import React, { useEffect, useState } from 'react';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import FormField from '../../components/common/FormField';
import {
  CompetitionClassDTO,
  CompetitionClassTypeDTO,
  CompetitionExerciseDTO,
  CompetitionExerciseTypeDTO,
  ExerciseSpecificDTO,
  GenderDTO,
} from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import {
  useFormValidation,
  ValidationRules,
} from '../../hooks/useFormValidation';
import { useCompetitionExerciseTypeApi } from '../../hooks/useCompetitionExerciseTypeApi';
import { useExerciseSpecificApi } from '../../hooks/useExerciseSpecificApi';
import { useCompetitionClassApi } from '../../hooks/useCompetitionClassApi';
import { useGenders } from '../../hooks/useGenderApi';
import { useCompetitionClassTypeApi } from '../../hooks/useCompetitionClassTypeApi';

interface CompetitionExerciseFormProps {
  initialValues: CompetitionExerciseDTO;
  onSubmit: (data: CompetitionExerciseDTO) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const FORM_SECTIONS = {
  basicInfo: {
    id: 'basic-info',
    title: 'Basic Information',
  },
} as const;

const CompetitionExerciseForm: React.FC<CompetitionExerciseFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [formData, setFormData] =
    useState<CompetitionExerciseDTO>(initialValues);

  const validationRules: ValidationRules = {
    exerciseSpecificTitle: [
      { required: true, message: 'Exercise Specific Title is required' },
    ],
  };

  const { errors, validate } =
    useFormValidation<CompetitionExerciseDTO>(validationRules);

  const [exerciseTypes, setExerciseTypes] = useState<
    CompetitionExerciseTypeDTO[]
  >([]);
  const { search: searchExerciseTypes, isLoading: isLoadingExerciseTypes } =
    useCompetitionExerciseTypeApi();
  const fetchExerciseTypes = async () => {
    try {
      const response = await searchExerciseTypes({
        page: 1,
        pageSize: 9999,
        sortBy: '',
        sortDescending: false,
      });
      setExerciseTypes(response?.data?.results || []);
    } catch (error) {
      console.error('Error fetching exercise types:', error);
    }
  };

  const [exerciseSpecifics, setExerciseSpecifics] = useState<
    ExerciseSpecificDTO[]
  >([]);
  const {
    search: searchExerciseSpecifics,
    isLoading: isLoadingExerciseSpecifics,
  } = useExerciseSpecificApi();
  const fetchExerciseSpecifics = async () => {
    try {
      const response = await searchExerciseSpecifics({
        page: 1,
        pageSize: 9999,
        sortBy: '',
        sortDescending: false,
      });
      setExerciseSpecifics(response?.data?.results || []);
    } catch (error) {
      console.error('Error fetching exercise specifics:', error);
    }
  };

  const [competitionClassTypes, setCompetitionClasseTypes] = useState<
    CompetitionClassTypeDTO[]
  >([]);
  const {
    search: searchCompetitionClasseTypes,
    isLoading: isLoadingCompetitionClasseTypes,
  } = useCompetitionClassTypeApi();
  const fetchCompetitionClasseTypes = async () => {
    try {
      const response = await searchCompetitionClasseTypes({
        page: 1,
        pageSize: 9999,
        sortBy: '',
        sortDescending: false,
      });
      setCompetitionClasseTypes(response?.data?.results || []);
    } catch (error) {
      console.error('Error fetching competition classe types:', error);
    }
  };

  const [genders, setGenders] = useState<
    GenderDTO[]
  >([]);
  const { data: gendersData, isLoading: isLoadingGenders } = useGenders();

  useEffect(() => {
    fetchExerciseTypes();
    fetchExerciseSpecifics();
    fetchCompetitionClasseTypes();
  }, []);

  const handleSave = () => {
    if (validate(formData)) {
      onSubmit(formData);
    }
  };

  const sections = Object.values(FORM_SECTIONS);

  return (
    <Form
      sections={sections}
      onSave={handleSave}
      onCancel={onCancel}
      isSubmitting={isLoading}
    >
      <FormSection
        id={FORM_SECTIONS.basicInfo.id}
        title={FORM_SECTIONS.basicInfo.title}
      >
        <FormField
          id="exerciseSpecificTitle"
          label="Title"
          type="text"
          value={formData.exerciseSpecificTitle || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Exercise Specific Title"
          required={false}
          error={errors.exerciseSpecificTitle}
        />
        <FormField
          id="competitionExerciseTypeId"
          label="Exercise Type"
          type="dropdown"
          value={formData.competitionExerciseTypeId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Select Competition Exercise Type"
          required={true}
          error={errors.competitionExerciseTypeId}
          options={exerciseTypes.map((type) => ({
            value: type.id,
            label: type.title || '',
          }))}
          isLoading={isLoadingExerciseTypes}
        />
        <FormField
          id="exerciseSpecificId"
          label="Exercise Specific"
          type="dropdown"
          value={formData.exerciseSpecificId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Select Exercise Specific"
          required={true}
          error={errors.exerciseSpecificId}
          options={exerciseSpecifics.map((type) => ({
            value: type.id,
            label: type.title || '',
          }))}
          isLoading={isLoadingExerciseSpecifics}
        />
        <FormField
          id="competitionClassTypeId"
          label="Competition Class"
          type="dropdown"
          value={formData.competitionClassTypeId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Select Competition Class Type"
          required={true}
          error={errors.competitionClassTypeId}
          options={competitionClassTypes.map((type) => ({
            value: type.id,
            label: type.title || '',
          }))}
          isLoading={isLoadingCompetitionClasseTypes}
        />
        <FormField
          id="genderId"
          label="Competition Gender"
          type="dropdown"
          value={formData.genderId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Select Gender"
          required={true}
          error={errors.genderId}
          options={(gendersData || []).map((type) => ({
            value: type.id,
            label: type.title || '',
          }))}
          isLoading={isLoadingGenders}
        />
        <FormField
          id="isActive"
          label="Is Active?"
          type="checkbox"
          value={formData.isActive || ''}
          onChange={(e) => handleChange(e, setFormData)}
          required={false}
          error={errors.isActive}
          helper='Check to make record active'
        />
      </FormSection>
    </Form>
  );
};

export default CompetitionExerciseForm;
